/**
 * Code 128 (subset B) as an SVG — the order barcode a laundry ticket and its
 * bag tag carry (2026-10-01). Subset B covers printable ASCII, so the value is
 * the invoice number as-is; any 1D scanner reads it, including the till's own
 * camera scanner (BarcodeDetector / zxing, both of which decode Code 128).
 *
 * No dependency: the symbol table below is the standard one (ISO/IEC 15417),
 * bar/space widths in modules. `code128.test.ts` in the cashier decodes the
 * output with zxing to prove it.
 */

/** Values 0–105 then STOP (106), as bar,space,bar,space,bar,space widths. */
const PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
] as const;
const START_B = 104;
const STOP = 106;

/** The symbol values for `text` in subset B, start and checksum included. */
export function code128Values(text: string): number[] {
  const values = [START_B];
  for (const ch of text) {
    const c = ch.charCodeAt(0);
    if (c < 32 || c > 126) throw new Error(`Code 128-B cannot encode character code ${c}`);
    values.push(c - 32);
  }
  const checksum = values.reduce((sum, v, i) => sum + v * (i === 0 ? 1 : i), 0) % 103;
  values.push(checksum, STOP);
  return values;
}

/** Bar/space widths in modules, left to right, starting with a bar. */
export function code128Modules(text: string): number[] {
  return code128Values(text).flatMap((v) => [...PATTERNS[v]!].map(Number));
}

/**
 * An SVG of the barcode with its human-readable text under it. `module` is
 * the narrowest bar in px; at the till's 72 mm print width a 2 px module keeps
 * a 6–7 digit invoice number comfortably inside the paper with quiet zones.
 */
export function code128Svg(text: string, opts: { module?: number; height?: number; showText?: boolean } = {}): string {
  const m = opts.module ?? 2;
  const h = opts.height ?? 56;
  const quiet = 10 * m;
  const widths = code128Modules(text);
  let x = quiet;
  const bars: string[] = [];
  widths.forEach((w, i) => {
    if (i % 2 === 0) bars.push(`<rect x="${x}" y="0" width="${w * m}" height="${h}"/>`);
    x += w * m;
  });
  const width = x + quiet;
  const textH = opts.showText === false ? 0 : 18;
  const label =
    opts.showText === false
      ? ''
      : `<text x="${width / 2}" y="${h + 15}" text-anchor="middle" font-family="monospace" font-size="15" fill="#000">${escapeXml(text)}</text>`;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${h + textH}" viewBox="0 0 ${width} ${h + textH}" ` +
    `shape-rendering="crispEdges"><g fill="#000">${bars.join('')}</g>${label}</svg>`
  );
}

function escapeXml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' })[c]!);
}

/**
 * Put the order barcode at the END of an already-built receipt document — the
 * built-in slip or a tenant's own design alike — without touching either
 * layout. Only a laundry order's ticket calls this, so every other receipt
 * prints exactly as before.
 */
export function appendOrderBarcode(docHtml: string, value: string, caption?: string): string {
  const block =
    `<div class="matix-order-barcode" style="text-align:center;margin:10px 0 4px;page-break-inside:avoid">` +
    (caption ? `<div style="font-size:13px;font-weight:700;margin-bottom:4px">${escapeXml(caption)}</div>` : '') +
    code128Svg(value) +
    `</div>`;
  const at = docHtml.search(/<\/body>/i);
  return at >= 0 ? docHtml.slice(0, at) + block + docHtml.slice(at) : docHtml + block;
}
